/**
 * Native bridge for the Expo WebView shell (Phase 3).
 *
 * The Expo shell loads wital.ai inside a React Native WebView and exposes
 * `window.ReactNativeWebView.postMessage` for the web side to call. Native
 * code handles the message (e.g. runs the Google or Apple SDK) and injects
 * the result back into the WebView via localStorage + reload.
 *
 * Outside the Expo shell, all helpers here are safe no-ops.
 */

interface ReactNativeWebViewBridge {
  postMessage(data: string): void
}

function rnWebView(): ReactNativeWebViewBridge | null {
  const w = window as unknown as { ReactNativeWebView?: ReactNativeWebViewBridge }
  return w.ReactNativeWebView ?? null
}

/**
 * Are we running inside the Expo React Native WebView shell?
 * True only when the shell has injected its bridge — not true for the PWA
 * in Safari/Chrome.
 */
export function isReactNativeWebView(): boolean {
  return rnWebView() !== null
}

/**
 * Post a structured message to the native layer. No-op in a plain browser.
 */
export function postToNative(msg: { type: string; [key: string]: unknown }): void {
  const bridge = rnWebView()
  if (bridge) {
    bridge.postMessage(JSON.stringify(msg))
  }
}

/**
 * Host platform when running inside the Expo shell ('ios' | 'android'),
 * or null in a plain browser. Set by the shell's injected preload
 * script (mobile/app/index.jsx :: buildInjectJs).
 */
export function nativePlatform(): 'ios' | 'android' | null {
  const w = window as unknown as { __nativePlatform?: string }
  const p = w.__nativePlatform
  return p === 'ios' || p === 'android' ? p : null
}
